import { type FC, useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import { importCanvas, parseCanvasJSON, ImportError } from '@/services/importCanvas';
import type { Canvas } from '@/types/canvas.types';

interface ImportCanvasButtonProps {
  workspaceId: string;
  onImported: (canvas: Canvas) => void;
}

export const ImportCanvasButton: FC<ImportCanvasButtonProps> = ({
  workspaceId,
  onImported,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;

    setBusy(true);
    setError(null);
    try {
      const text = await file.text();
      const parsed = parseCanvasJSON(text);
      const canvas = await importCanvas(parsed, workspaceId);
      onImported(canvas);
    } catch (err) {
      const msg =
        err instanceof ImportError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Échec de l'import";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <Button onClick={handleClick} disabled={busy} variant="secondary">
        {busy ? 'Import…' : 'Importer JSON'}
      </Button>
      {error && (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: '#EF4444',
            marginLeft: 'var(--spacing-sm)',
          }}
        >
          {error}
        </span>
      )}
    </>
  );
};

export default ImportCanvasButton;
